/**
 * Route ID Utilities
 * Tolerant parsing of entity ids arriving in route params.
 *
 * The real backend emits prefixed string ids like "TRN-0001" in list
 * responses while detail endpoints take numeric ids, so a plain
 * parseInt(param) yields NaN and the ahooks `ready` gate then suppresses
 * the detail request entirely (drift D-VEND-NEW-1). Accept both shapes;
 * reject everything else so garbage never resolves to a real record
 * (the digit-scavenging trap flagged in drift D-VEN-9).
 */

// Letter prefix + dash + digits, e.g. "TRN-0001" / "VEN-12".
// Deliberately excludes digit-leading composites like "1-222334"
// (category-company ids, a different id space — drift D-CAT-11).
const PREFIXED_ID_PATTERN = /^[A-Za-z]{2,4}-0*(\d+)$/;

/**
 * Parse an entity id from a route param into the numeric id the detail
 * APIs expect. Returns null when the param is absent or not a
 * recognizable id (callers render their not-found state, same as today).
 */
export function parseEntityRouteId(param: string | undefined | null): number | null {
  if (!param) return null;

  const digits = /^\d+$/.test(param) ? param : PREFIXED_ID_PATTERN.exec(param)?.[1];
  if (!digits) return null;

  const id = Number(digits);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}
